import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import { ALL_POLICIES } from "@/data/policies";

export default function PolicyPage({ slug }) {
  const policy = ALL_POLICIES.find((p) => p.slug === slug);
  if (!policy) return null;

  return (
    <div data-testid={`policy-${slug}-page`} className="bg-[#050505] text-[#F3E8E0]">
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden grain-bg">
        <div className="absolute top-32 left-1/3 w-[500px] h-[500px] bg-[#FF4A00] opacity-[0.06] rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <Link
            to="/"
            data-testid="policy-back"
            className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#FF4A00] transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <SectionLabel className="mb-5">Legal</SectionLabel>
            <h1
              data-testid={`policy-${slug}-title`}
              className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95] max-w-4xl"
            >
              {policy.title}
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-[#A3A3A3]">
              <span>Effective: {policy.effective}</span>
              <span className="hidden sm:inline">·</span>
              <a
                href="mailto:contact@incer.in"
                className="text-[#FF4A00] hover:underline inline-flex items-center gap-1"
              >
                Questions? contact@incer.in <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 md:pb-32 border-t border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Sidebar nav */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <div className="label-eyebrow mb-4">On this page</div>
                <nav className="flex lg:flex-col gap-1 flex-wrap">
                  {policy.sections.map((s, i) => (
                    <a
                      key={i}
                      href={`#section-${i}`}
                      data-testid={`policy-nav-${i}`}
                      className="text-sm text-[#A3A3A3] hover:text-[#FF4A00] transition-colors py-1.5 lg:py-2 px-3 lg:px-0 rounded-full lg:rounded-none border lg:border-0 border-[#1a1a1a] hover:border-[#FF4A00]"
                    >
                      {s.h}
                    </a>
                  ))}
                </nav>
                <div className="hidden lg:block mt-8 p-5 rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a]">
                  <div className="label-eyebrow mb-2">Other policies</div>
                  <ul className="space-y-2 mt-3">
                    {ALL_POLICIES.filter((p) => p.slug !== slug).map((p) => (
                      <li key={p.slug}>
                        <Link
                          to={`/${p.slug}`}
                          data-testid={`policy-related-${p.slug}`}
                          className="text-sm text-[#F3E8E0] hover:text-[#FF4A00] transition-colors inline-flex items-center gap-1.5 group"
                        >
                          {p.title}
                          <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>

            {/* Body */}
            <article className="lg:col-span-9 max-w-3xl">
              <p className="text-lg md:text-xl text-[#F3E8E0] leading-relaxed">{policy.intro}</p>

              {policy.sections.map((s, i) => (
                <motion.div
                  key={i}
                  id={`section-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="mt-12 pt-10 border-t border-[#1a1a1a] scroll-mt-32"
                  data-testid={`policy-section-${i}`}
                >
                  <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-[#F3E8E0] mb-5">
                    {s.h}
                  </h2>
                  <div className="space-y-4 text-base md:text-lg text-[#A3A3A3] leading-relaxed">
                    {s.p.map((para, j) => (
                      <p key={j}>{para}</p>
                    ))}
                  </div>
                </motion.div>
              ))}

              <div className="mt-16 pt-10 border-t border-[#1a1a1a] flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <p className="text-sm text-[#666]">
                  © {new Date().getFullYear()} Incer Technovation Pvt Ltd.
                </p>
                <Link
                  to="/contact"
                  data-testid="policy-cta-contact"
                  className="inline-flex items-center gap-1.5 text-[#FF4A00] hover:gap-2.5 transition-all text-sm font-medium"
                >
                  Need to talk to us? <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
}
